import { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Spinner } from '@/components/ui'
import { listMine, type ReportRead } from '@/services/helpFeedback/appFeedbackApi'
import { useHelpFeedbackStore } from '@/stores/helpFeedbackStore'
import {
  KIND_WORDS, MINE_EMPTY, MINE_FAILED, MINE_LOADING, MINE_PRIVACY, STATUS_WORDS, TRY_AGAIN, agoText,
} from './helpFeedbackText'

type Load = { state: 'loading' } | { state: 'failed' } | { state: 'ready'; rows: ReportRead[] }

/** Your feedback (SA-1310 spec §11): what you sent and where it stands, read each time the tab opens. */
export function YourFeedbackTab() {
  const tab = useHelpFeedbackStore((s) => s.tab)
  const open = useHelpFeedbackStore((s) => s.open)
  const [load, setLoad] = useState<Load>({ state: 'loading' })

  const read = useCallback(() => {
    let live = true
    setLoad({ state: 'loading' })
    listMine()
      .then((rows) => live && setLoad({ state: 'ready', rows }))
      .catch(() => live && setLoad({ state: 'failed' }))
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    if (!open || tab !== 'mine') return
    return read()
  }, [open, tab, read])

  return (
    <div className="space-y-4 px-5 py-4 text-sm">
      {load.state === 'loading' && (
        <p className="flex items-center gap-2 text-muted-foreground" role="status">
          <Spinner className="size-4" aria-hidden />
          {MINE_LOADING}
        </p>
      )}
      {load.state === 'failed' && (
        <div className="space-y-2">
          <p>{MINE_FAILED}</p>
          <Button size="sm" variant="outline" onClick={() => read()}>{TRY_AGAIN}</Button>
        </div>
      )}
      {load.state === 'ready' && load.rows.length === 0 && <p className="text-muted-foreground">{MINE_EMPTY}</p>}
      {load.state === 'ready' && load.rows.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-section-border bg-card shadow-xs">
          {load.rows.map((r) => {
            const status = STATUS_WORDS[r.status] ?? STATUS_WORDS.new
            return (
              <li key={r.id} className="space-y-1 px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span className="font-mono text-foreground">{r.reference}</span>
                  {r.kind && <span>{KIND_WORDS[r.kind]}</span>}
                  <span>{agoText(Date.now() - Date.parse(r.sent_at))}</span>
                  <Badge variant={status.tone} className="ml-auto">{status.label}</Badge>
                </div>
                {r.summary && <p className="line-clamp-2">{r.summary}</p>}
              </li>
            )
          })}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">{MINE_PRIVACY}</p>
    </div>
  )
}
